/** @jsxImportSource @emotion/react */
/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MikeButton from '../mike-button';
import CloseS from '../../icons/CloseS.svg?react';
import Typography from '@mui/material/Typography';
import { Provider } from '../../generated/src/models';
import DTU10 from './ProviderData/DTU10';
import CMEMS from './ProviderData/CMEMS';
import HYCOM from './ProviderData/HYCOM';
import NOAA from './ProviderData/NOAA';
import ERA5 from './ProviderData/ERA5';
import DHIGWM from './ProviderData/DHIGWM';
import ERA5WAVEMODELLING from './ProviderData/ERA5WAVEMODELLING'
import PdfConverter from './ProviderExporter/PDFConverter';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { closeButtonStyle, contentWrapStyle, dialogBottomStyle, rootStyle, scrollPaperStyle } from './ProviderDialogStyles';
import HYCOM_V02 from './ProviderData/HYCOM_V02';

export function DialogTitle({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  
  return (
    <MuiDialogTitle sx={rootStyle}>
      <Typography variant="h6" component="span">{children} </Typography>
      {onClose && (
        <span css={closeButtonStyle} onClick={onClose}>
          <CloseS />
        </span>
      )}
    </MuiDialogTitle>
  );
}

export enum ProviderNames {
  DTU10 = 'DTU 10 Tide',
  CMEMS = 'Copernicus',
  HYCOM = 'HYCOM',
  NOAA = 'NOAA CFSv2',
  DHIGWM = 'DHI GWM',
  ERA5 = 'ERA5 2D',
  ERA5_MOOD = 'ERA5',
  ERA5_WAVEMODELLING = "DHI Global ERA 5 10m corrected",
  ERA5_WAVEMODELLING_DEV = "DHI ERA 5 10m corrected",
  HYCOM_V02="HYCOM ESPC-D-V02"
}

export default function ProviderDialog({
  open,
  onClose,
  provider,
}: {
  open: boolean;
  onClose?: () => void;
  provider: Provider | undefined;
}) {
  const _onClose = () => {
    if (onClose) onClose();
  };  

  const getDialogContent = (providerName: string) => {
    switch (providerName) {
      case ProviderNames.ERA5_WAVEMODELLING:
      case ProviderNames.ERA5_WAVEMODELLING_DEV:
        return <ERA5WAVEMODELLING />;
      case ProviderNames.DTU10:
        return <DTU10 />;
      case ProviderNames.CMEMS:
        return <CMEMS />;
      case ProviderNames.HYCOM:
        return <HYCOM />;
      case ProviderNames.NOAA:
        return <NOAA />;
      case ProviderNames.ERA5:
      case ProviderNames.ERA5_MOOD:
        return <ERA5 />;
      case ProviderNames.HYCOM_V02:
        return <HYCOM_V02/>;
      default: 
        if (providerName.startsWith(ProviderNames.DHIGWM)){
          return <DHIGWM />;
        }
        else{
          return null;
        }  
    }
  };


  return (
    <Dialog
      open={open}
      onClose={_onClose}
      maxWidth={'md'}
      sx={{
        paper: scrollPaperStyle,
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle onClose={_onClose}>{provider?.displayName}</DialogTitle>
      <DialogContent sx={contentWrapStyle}>
        {provider && provider.name ? getDialogContent(provider.name) : ''}
      </DialogContent>
      <DialogFooter>    
        <MikeButton variant={"contained"} style={{ width: 328 }} tooltipTitle="Export content and download as PDF-file">
          <PDFDownloadLink
            style={{ color: 'white', textDecoration: 'none'}}
            document={
              <PdfConverter providerName={provider && provider.name ? provider.name : ''} />
            }
            fileName="download.pdf"              
            >
              {"Download"}           
          </PDFDownloadLink>  
        </MikeButton>      
      </DialogFooter>
    </Dialog>
  );
}

export function DialogFooter({ children }: { children: React.ReactNode }) {  
  return <DialogActions sx={dialogBottomStyle}>{children}</DialogActions>;
}
